import { useState, useCallback, useMemo, useEffect } from 'react';
import * as Store from '@apply/store';
import { FeatureType } from '@apply/constants';
import { logApplicationInsightsTrace } from '../../common/functions/applicationInsightFunctions';

export type AuthenticationHandler = (
    email: string,
    password: string,
    ipaddress: string
) => Promise<{ authentication?: Store.UserStore.Types.Authentication; err?: string }>;

export type LoginResult = {
    authentication?: Store.UserStore.Types.Authentication;
    err?: string;
};

export type UseAuthenticationResult = {
    login: (
        username: string,
        password: string,
        ipaddress: string,
        handler: AuthenticationHandler
    ) => Promise<LoginResult>;
    logout: () => void;
    isAuthenticated: boolean;
    isRegisteredUser: boolean;
    authentication: Store.UserStore.Types.Authentication | undefined;
    isLocEnabled: boolean;
    requiresESign: boolean;
    isFeatureEnabled: (featureType: FeatureType) => boolean;
};

export const useAuthentication = (): UseAuthenticationResult => {
    const [authentication, setAuthentication] = useState<Store.UserStore.Types.Authentication>();
    const isAuthenticated = useMemo(() => Store.UserStore.Types.isAuthenticated(authentication), [authentication]);
    const isRegisteredUser = useMemo(() => Store.UserStore.Types.isRegisteredUser(authentication), [authentication]);
    const isLocEnabled = useMemo(() => Store.UserStore.Types.isLocEnabled(authentication), [authentication]) ?? false;
    const requiresESign = useMemo(() => Store.UserStore.Types.requiresESign(authentication), [authentication]) ?? false;

    const isFeatureEnabled = (featureType: FeatureType) => {
        const auth = Store.UserStore.Types.retrieve();
        if (!auth) {
            return false;
        }
        return Store.UserStore.Types.isFeatureEnabled(auth.user, featureType) ?? false;
    };

    useEffect(() => {
        const stored = Store.UserStore.Types.retrieve();
        if (stored) {
            setAuthentication(stored);
        }
    }, []);

    const login = useCallback(
        async (myUsername: string, password: string, ipaddress: string, handler: AuthenticationHandler): Promise<LoginResult> => {
            if (!myUsername || !password || !handler) {
                return {};
            }

            try {
                const result = await handler(myUsername, password, ipaddress);
                if (!result.authentication) {
                    return { err: result.err };
                }

                Store.UserStore.Types.store(result.authentication);
                setAuthentication(result.authentication);
                return { authentication: result.authentication };
            } catch {
                return {};
            }
        },
        []
    );

    const logout = useCallback(() => {
        const stackTrace = Error().stack;
        logApplicationInsightsTrace('logout', { stackTrace });
        Store.UserStore.Types.clear();
        window.sessionStorage.clear();
        setAuthentication(undefined);
    }, []);

    return {
        login,
        logout,
        isAuthenticated,
        isRegisteredUser,
        authentication,
        isLocEnabled,
        requiresESign,
        isFeatureEnabled,
    };
};
